import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://ruempel.taxi";
const DEFAULT_TITLE = "Rümpeltaxi | Entrümpelung, Haushaltsauflösung und Entsorgung";
const DEFAULT_DESC =
  "Rümpeltaxi unterstützt Sie bei Entrümpelungen, Haushaltsauflösungen, Entsorgungen, Umzügen und Einlagerungen. Persönlich, diskret und zuverlässig.";

export default function Seo({
  title,
  description,
  path = "/",
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title || DEFAULT_TITLE;
  const desc = description || DEFAULT_DESC;
  const url = `${SITE_URL}${path}`;
  const ldArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  // React 19 hoistet die Tags nativ – statische Fallback-Tags aus index.html entfernen (keine Duplikate)
  useEffect(() => {
    document.querySelectorAll("[data-static-seo]").forEach((el) => el.remove());
  }, []);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Rümpeltaxi" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={`${SITE_URL}/og-image.png`} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />

      {ldArray.map((ld, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(ld)}
        </script>
      ))}
    </Helmet>
  );
}

export const localBusinessLd = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "Rümpeltaxi",
  legalName: "Thomas Dietrich – Rümpeltaxi",
  founder: "Thomas Dietrich",
  description: DEFAULT_DESC,
  url: SITE_URL,
  telephone: "+4915170004440",
  email: "ruempeltaxi@gmail.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Friedenstraße 10",
    postalCode: "89281",
    addressLocality: "Altenstadt",
    addressRegion: "Bayern",
    addressCountry: "DE",
  },
  areaServed: [
    "Ulm", "Neu-Ulm", "Illertissen", "Altenstadt", "Biberach", "Heidenheim",
    "Göppingen", "Memmingen", "Allgäu", "Günzburg", "Stuttgart", "Ludwigsburg", "Heilbronn",
  ],
  image: `${SITE_URL}/og-image.png`,
};

export const buildBreadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: it.label,
    item: `${SITE_URL}${it.to}`,
  })),
});

export const buildFaqLd = (faq) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

// Lokale Service-Auszeichnung für Ortsseiten (Google Local + KI-Suche).
export const buildLocalServiceLd = (city, url) => {
  const services = city.services && city.services.length
    ? city.services
    : ["Entrümpelung", "Haushaltsauflösung", "Räumung", "Entsorgung", "Umzug"];
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: `Entrümpelung und Haushaltsauflösung in ${city.name}`,
    name: `Entrümpelung ${city.name} – Rümpeltaxi`,
    description: city.meta_description,
    url: `${SITE_URL}${url}`,
    provider: {
      "@type": "ProfessionalService",
      name: "Rümpeltaxi",
      legalName: "Thomas Dietrich – Rümpeltaxi",
      telephone: "+4915170004440",
      email: "ruempeltaxi@gmail.com",
      url: SITE_URL,
      image: `${SITE_URL}/og-image.png`,
      address: {
        "@type": "PostalAddress",
        streetAddress: "Friedenstraße 10",
        postalCode: "89281",
        addressLocality: "Altenstadt",
        addressCountry: "DE",
      },
    },
    areaServed: {
      "@type": "City",
      name: city.name,
      ...(city.postal_codes && city.postal_codes.length ? { postalCode: city.postal_codes.join(", ") } : {}),
      ...(city.state ? { addressRegion: city.state } : {}),
      address: { "@type": "PostalAddress", addressLocality: city.name, addressCountry: "DE" },
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: `Leistungen in ${city.name}`,
      itemListElement: services.map((s) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: `${s} ${city.name}` },
      })),
    },
  };
};
